import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { RootState } from '../../../shared/redux/reducers';
import { getModuleName } from '../../shared/services/reservation.service';

import {
  getOneReservations,
  addGuest,
  updateOneReservations,
  reactivateOneReservations,
  addOneComment,
  splitOneReservation
} from '../../shared/services/data.service';
import { ReservationDetailsUiModel } from '../../../shared/ui-models/reservation.ui-model';
import { ReservationStatus } from '../../../shared/enums/reservation-status';
import { GuestUiModel } from '../../../shared/ui-models/guest.ui-model';
import moment from 'moment';
import { AddGuestModal } from './components/add-guest-modal/add-guest-modal.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { AddCommentModal } from './components/add-comment-modal/add-comment-modal.component';
import { BookOfferResponseModel } from '../../../shared/ui-models/book.ui-model';
import { SelectAvailableRoomModal } from './components/select-available-room-modal/select-available-room-modal.component';
import { BookingRequestUiModel } from '../../../shared/ui-models/booking-request.ui-model';
import { getBookingRequestDetails } from '../../../shared/services/data/booking.data';
import { UserRole } from '../../../shared/enums/user-role.enum';
import { ReservationHeader } from './components/reservation-header/reservation-header.component';
import { CommentsModal } from './components/comments-modal/comments-modal.component';
import { HistoryModal } from './components/history-modal/history-modal.component';
import { FinancialAdjustments } from './components/financial-adjustments/financial-adjustments.component';
import { GuestSection } from './components/guest-section/guest-section.component';
import { PaymentsSection } from './components/payments-section/payments-section.component';
import { SplitReservationModal } from './components/split-reservation-modal/split-reservation-modal.component';
import { ResendGuestEmailModal } from './components/resend-guest-email-modal/resend-guest-email-modal.component';
import { SectionNav } from './components/section-nav/section-nav.component';
import { PaymentSummary } from './components/payment-summary/payment-summary.component';
import { StaySection } from './components/stay-section/stay-section.component';
import { HomeownerSection } from './components/homeowner-section/homeowner-section.component';
import { SourceSection } from './components/source-section/source-section.component';

const SECTIONS = [
  { id: 'reservation-stay', label: 'Stay' },
  { id: 'reservation-guests', label: 'Guests' },
  { id: 'reservation-payments', label: 'Payments' },
  { id: 'reservation-adjustments', label: 'Adjustments' },
  { id: 'reservation-homeowner', label: 'Homeowner' },
  { id: 'reservation-source', label: 'Source' }
];

const CONTENT_CLASS = 'max-w-[1440px] mx-auto px-[clamp(16px,3vw,48px)]';
const SECTION_CLASS = 'py-[40px] scroll-mt-[72px]';

const ReservationsEdit = (props: any) => {
  const { match, history, userId, userRole, userRoles } = props;
  const [reservation, setReservation] = useState<ReservationDetailsUiModel>();
  const [bookoffer, setBookoffer] = useState<BookOfferResponseModel>();
  const [bookofferLoading, setBookofferLoading] = useState<boolean>(false);
  const [reservationStatus, setReservationStatus] = useState<string>();
  const [showAddGuestModal, setShowAddGuestModal] = useState<boolean>(false);
  const [showAddCommentModal, setShowAddCommentModal] = useState<boolean>(false);
  const [showReactivateModal, setShowReactivateModal] = useState<boolean>(false);
  const [commentValue, setCommentValue] = useState<string>('');
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [skipEmail, setSkipEmail] = useState<boolean>(false);
  const [contactInfo, setContactInfo] = useState<BookingRequestUiModel | undefined>();
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false);
  const [showSplitReservationModal, setShowSplitReservationModal] = useState<boolean>(false);
  const [splitReservationLoading, setSplitReservationLoading] = useState<boolean>(false);
  const [splitReservationError, setSplitReservationError] = useState<string | undefined>(undefined);
  const [showResendEmailModal, setShowResendEmailModal] = useState<boolean>(false);
  const [showChangeDatesModal, setShowChangeDatesModal] = useState<boolean>(false);
  const [showSwitchRoomModal, setShowSwitchRoomModal] = useState<boolean>(false);

  const isPermited = (permittedList: UserRole[]) => {
    return (
      permittedList.indexOf(userRole) > -1 ||
      permittedList.some((item) => userRoles?.includes(item))
    );
  };

  const isCanEditDates = isPermited([
    UserRole.RESERVATION_MANAGER,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  const getBookingDetails = useCallback((requestId: string) => {
    getBookingRequestDetails(requestId, getModuleName()).then((requestItem) => {
      if (!isErrorInfo(requestItem)) {
        setContactInfo(requestItem);
      } else {
        birdToast.error('No available room for the selected dates');
      }
    });
  }, []);

  const getOneReservation = (id: string) => {
    return getOneReservations(id).then((response: ReservationDetailsUiModel | ErrorInfo): any => {
      if (!isErrorInfo(response)) {
        // const isHospitalityLead = userRole === UserRole.HOSPITALITY_LEAD;
        // const isAssignedPropertyManager = response.propertyManagersList?.some(
        //   (propertyManager) => propertyManager.id === userId
        // );

        // if (isHospitalityLead && !isAssignedPropertyManager) {
        //   birdToast.error('You do not have access to this reservation.');
        //   history.replace(APP_ROUTES.OPERATION_MODULE.FULL_PATH);
        //   return;
        // }

        if (response && response.paymentList && response.paymentList.length > 0) {
          response = {
            ...response,
            paymentList: response.paymentList
              .filter(Boolean)
              .sort((a, b) => b.paymentAmount - a.paymentAmount)
              .sort(
                (a, b) =>
                  moment(a.dueDate).hours(0).seconds(0).milliseconds(0).unix() -
                  moment(b.dueDate).hours(0).seconds(0).milliseconds(0).unix()
              )
          };
        }
        setReservation(response);
        setReservationStatus(response.status);
        getBookingDetails(response.bookingRequestId);
      }
    });
  };

  useEffect(() => {
    getOneReservation(match.params.id);
  }, []);

  const handleSubmitComment = async (value: string) => {
    if (!reservation?.id) return;

    setIsSubmittingComment(true);
    try {
      const response = await addOneComment(reservation.id, value);
      if (isErrorInfo(response)) {
        birdToast.error('Failed to add message');
        return;
      }

      await getOneReservation(reservation.id);
      birdToast.success('New message added successfully.');
      // setShowCommentsModal(false);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleSplitReservation = (checkInDate?: number, checkOutDate?: number) => {
    if (!reservation?.id) return;

    setSplitReservationLoading(true);
    setSplitReservationError(undefined);
    splitOneReservation(reservation.id, { checkInDate, checkOutDate }).then((response) => {
      setSplitReservationLoading(false);
      if (isErrorInfo(response)) {
        setSplitReservationError(
          response.errorMessage || 'Failed to split reservation. Please try again.'
        );
      } else {
        setShowSplitReservationModal(false);
        setSplitReservationError(undefined);
        getOneReservation(reservation.id);
        birdToast.success('Reservation split successfully.');
      }
    });
  };

  return (
    <div className="min-h-[calc(100vh-45px)] bg-neutral-0 font-gilroy-regular text-neutral-500">
      {reservation && (
        <>
          <div className={`${CONTENT_CLASS} pt-[32px] pb-[24px]`}>
            <ReservationHeader
              data={{
                reservation,
                reservationStatus,
                bookofferLoading,
                canChangeDates: isCanEditDates,
                showSwitchRoomModal
              }}
              eventHandlers={{
                onStatusChange: (newStatus) => {
                  setReservationStatus(newStatus);
                  setCommentValue('');
                  setCancellationReason('');
                  setSkipEmail(false);
                },
                onOpenAddCommentModal: () => setShowAddCommentModal(true),
                onReactivateModalOpen: (offer) => {
                  setBookoffer(offer);
                  setShowReactivateModal(true);
                },
                onBookofferLoadingChange: setBookofferLoading,
                onRefresh: getOneReservation,
                onOpenSplitReservationModal: () => setShowSplitReservationModal(true),
                onOpenChangeDatesModal: () => setShowChangeDatesModal(true),
                onOpenResendEmailModal: () => setShowResendEmailModal(true),
                onOpenCommentsModal: () => setShowCommentsModal(true),
                onOpenHistoryModal: () => setShowHistoryModal(true),
                onSwitchRoomModalChange: setShowSwitchRoomModal
              }}
            />
          </div>

          <SectionNav data={{ items: SECTIONS }} />

          <div className={`${CONTENT_CLASS} pb-[80px]`}>
            <div className="pt-[24px]">
              <PaymentSummary data={{ reservation }} />
            </div>

            <div className="divide-y divide-gray-200">
              <section id="reservation-stay" className={SECTION_CLASS}>
                <StaySection
                  data={{ reservation, canEditDates: isCanEditDates, showChangeDatesModal }}
                  eventHandlers={{
                    onRefresh: getOneReservation,
                    onChangeDatesModalChange: setShowChangeDatesModal,
                    onOpenSwitchRoomModal: () => setShowSwitchRoomModal(true),
                    onOpenSplitReservationModal: () => setShowSplitReservationModal(true)
                  }}
                />
              </section>

              <section id="reservation-guests" className={SECTION_CLASS}>
                <GuestSection
                  data={{
                    reservation,
                    leadBooker: {
                      name: contactInfo?.guestInfo?.name || reservation.primaryGuestName,
                      phoneNumber: contactInfo?.guestInfo?.phoneNumber,
                      email: contactInfo?.guestInfo?.email || reservation.primaryGuestEmail
                    }
                  }}
                  eventHandlers={{
                    onAddGuest: () => setShowAddGuestModal(true),
                    onGetOneReservation: getOneReservation
                  }}
                />
              </section>

              <section id="reservation-payments" className={SECTION_CLASS}>
                <PaymentsSection
                  data={{ reservation, contactEmail: contactInfo?.guestInfo?.email || '' }}
                  config={{ isPermited, userRole, userRoles }}
                  eventHandlers={{ onGetOneReservation: getOneReservation }}
                />
              </section>

              <section id="reservation-adjustments" className={SECTION_CLASS}>
                <FinancialAdjustments data={{ reservation }} config={{ isPermited }} />
              </section>

              <section id="reservation-homeowner" className={SECTION_CLASS}>
                <HomeownerSection data={{ reservation }} />
              </section>

              <section id="reservation-source" className={SECTION_CLASS}>
                <SourceSection data={{ reservation }} />
              </section>

              <div className="py-[32px] flex items-center gap-x-[12px] gap-y-[4px] flex-wrap text-[14px] text-neutral-300">
                <span>
                  Created by {reservation.createdByUsername} on{' '}
                  {moment(reservation.createdAt).format('DD/MM/YYYY HH:mm')}
                </span>
                {reservation.modifiedByUsername && (
                  <>
                    <span>·</span>
                    <span>
                      Last modified by {reservation.modifiedByUsername} on{' '}
                      {moment(reservation.updatedAt).format('DD/MM/YYYY HH:mm')}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <AddCommentModal
            config={{
              showModal: showAddCommentModal,
              showSkipEmail:
                reservationStatus === ReservationStatus.CHECKED_IN ||
                reservationStatus === ReservationStatus.IN_HOUSE,
              isCancellation: reservationStatus === ReservationStatus.CANCELED
            }}
            eventHandlers={{
              handelChange: (value) => {
                setCommentValue(value);
              },
              handleReasonChange: (value) => {
                setCancellationReason(value);
              },
              handelSkipEmail: (value) => {
                setSkipEmail(value);
              },
              closeHandler: () => {
                setShowAddCommentModal(false);
              },
              ChangeStatusWithComment: () => {
                const statusToUpdate = reservationStatus ? reservationStatus : reservation.status;

                setShowAddCommentModal(false);
                updateOneReservations({
                  id: reservation.id,
                  unitTypeId: reservation.unitTypeId,
                  bookingRequestId: reservation.bookingRequestId,
                  status: statusToUpdate,
                  cancellationReason:
                    statusToUpdate === ReservationStatus.CANCELED ? cancellationReason : undefined,
                  comment: commentValue ? commentValue : '',
                  cancellationComment: commentValue ? commentValue : '',
                  skipEmail: skipEmail ? skipEmail : false
                }).then(() =>
                  getOneReservation(reservation.id).then(() => setShowAddGuestModal(false))
                );
              }
            }}
          />

          <CommentsModal
            config={{ showModal: showCommentsModal, isSubmitting: isSubmittingComment }}
            data={{ commentsList: reservation.messages || [] }}
            eventHandlers={{
              onClose: () => setShowCommentsModal(false),
              onSubmit: handleSubmitComment
            }}
          />

          <HistoryModal
            config={{ showModal: showHistoryModal }}
            data={{ reservationId: reservation.id }}
            eventHandlers={{ onClose: () => setShowHistoryModal(false) }}
          />

          <SplitReservationModal
            config={{ showModal: showSplitReservationModal }}
            data={{
              reservation,
              isLoading: splitReservationLoading,
              errorMessage: splitReservationError
            }}
            eventHandlers={{
              closeHandler: () => {
                setShowSplitReservationModal(false);
                setSplitReservationError(undefined);
              },
              submitHandler: handleSplitReservation
            }}
          />

          <ResendGuestEmailModal
            config={{ showModal: showResendEmailModal }}
            data={{
              reservation,
              guestEmail: contactInfo?.guestInfo?.email || reservation.primaryGuestEmail || ''
            }}
            eventHandlers={{
              closeHandler: () => setShowResendEmailModal(false)
            }}
          />

          <SelectAvailableRoomModal
            config={{ showModal: showReactivateModal }}
            data={{ availableRooms: bookoffer?.availableRooms || [] }}
            eventHandlers={{
              submitRoomId: (value) => {
                reactivateOneReservations(reservation.id, value).then(() =>
                  getOneReservation(reservation.id).then(() => setShowReactivateModal(false))
                );
              },
              closeHandler: () => {
                setShowReactivateModal(false);
              }
            }}
          />
        </>
      )}
      <AddGuestModal
        config={{ showModal: showAddGuestModal }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddGuestModal(false);
          },
          addGuestHandler: (guest) => {
            addGuest(guest)
              .then((response: GuestUiModel | ErrorInfo) => {
                if (!isErrorInfo(response)) {
                  return response;
                }
              })
              .then((newGuest: GuestUiModel | undefined) => {
                if (reservation && newGuest) {
                  let updatedReservation: Partial<ReservationDetailsUiModel> = {
                    id: reservation.id
                  };
                  if (Array.isArray(reservation.guestIds)) {
                    updatedReservation = {
                      ...updatedReservation,
                      guestIds: [...reservation.guestIds, newGuest.id || '']
                    };
                  } else {
                    updatedReservation = {
                      ...updatedReservation,
                      guestIds: [newGuest.id || '']
                    };
                  }
                  updateOneReservations(updatedReservation).then(() =>
                    getOneReservation(reservation.id).then(() => setShowAddGuestModal(false))
                  );
                }
              });
          }
        }}
      />
    </div>
  );
};

export default connect(mapStateToProps)(ReservationsEdit);
function mapStateToProps(state: RootState) {
  return {
    isAuthenticated: !!state.auth.accessToken,
    permissions: state.auth.permissions,
    userId: state.auth.userId,
    userRole: state.auth.role,
    userRoles: state.auth.roles
  };
}
